import React from 'react';

import { Add, People, WorkHistory } from '@mui/icons-material';
import { BottomNavigation, BottomNavigationAction, Box, Paper, Stack, useTheme } from '@mui/material';
import { first, isNil, trimEnd } from 'lodash';
import type { Location } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { AppContext } from '../contexts/app_context';
import { createScopedI18n } from '../i18n/i18n';
import { filterAvailableCashbacks } from './cashback_info';
import AccountClockIcon from './icons/account_clock';
import Protector from './protector';
import useRestaurantCashbacks from './use_restaurant_cashbacks';

const bottomTabI18n = createScopedI18n('components.bottom_tab');

export type BottomTabProps = { location?: Location };

export const BottomTab = ({ location }: BottomTabProps) => {
  const theme = useTheme();
  const { contextCurrentRestaurant } = React.useContext(AppContext);

  const { cashbacks } = useRestaurantCashbacks(contextCurrentRestaurant?.id);
  const availableCashbacks = React.useMemo(() => filterAvailableCashbacks(cashbacks), [cashbacks]);
  const firstAvailableCashback = first(availableCashbacks);
  const cashbackLabel = isNil(firstAvailableCashback)
    ? null
    : bottomTabI18n('cashback_label', { count: firstAvailableCashback.amount });

  return (
    <>
      <Stack>
        <BottomNavigation /* Use as spacer to push content out of BottomNavigation */ />
      </Stack>

      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={5}>
        <BottomNavigation showLabels value={trimEnd(location?.pathname, '/')}>
          <BottomNavigationAction
            component={Link}
            to="/jobs"
            replace
            value="/jobs"
            label={bottomTabI18n('jobs_tab')}
            icon={<People />}
          />
          <Protector permissions={['jobCreate']}>
            <Protector.Allowed>
              <BottomNavigationAction
                component={Link}
                to="/jobs/new"
                value="/jobs/new"
                showLabel
                label={
                  cashbackLabel ? (
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        width: '100%',
                        maxWidth: '100%',
                        fontSize: '0.62rem',
                        fontWeight: 'normal',
                        lineHeight: 1.1,
                        opacity: 0.9,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textAlign: 'center',
                      }}
                    >
                      {cashbackLabel}
                    </Box>
                  ) : (
                    ''
                  )
                }
                icon={<Add />}
                sx={{
                  marginY: 0.5,
                  borderRadius: 1,
                  color: theme.palette.primary.contrastText,
                  backgroundColor: theme.palette.primary.main,
                }}
              />
            </Protector.Allowed>
            <Protector.Rejected>
              <BottomNavigationAction
                disabled={true}
                value="/jobs/new"
                label={bottomTabI18n('create_job_button')}
                icon={<Add />}
                sx={{ marginY: 0.5, borderRadius: 1, color: 'white', backgroundColor: 'gray' }}
              />
            </Protector.Rejected>
          </Protector>
          <BottomNavigationAction
            component={Link}
            to="/job_hirings"
            replace
            value="/job_hirings"
            label={bottomTabI18n('job_hirings_tab')}
            icon={<WorkHistory />}
          />
          <BottomNavigationAction
            component={Link}
            to="/attendances"
            value="/attendances"
            label={bottomTabI18n('attendances_tab')}
            icon={<AccountClockIcon />}
          />
        </BottomNavigation>
      </Paper>
    </>
  );
};
